import searchProductsFromBrand from "@faire/web-api/api/v2/search/products/from-brand/post";
import { IBrand } from "@faire/web-api/indigofair/data/IBrand";
import { ILeanProductTile } from "@faire/web-api/indigofair/data/ILeanProductTile";
import { ISearchMakerProductsRequest } from "@faire/web-api/indigofair/data/ISearchMakerProductsRequest";
import { ISearchProductsFromBrandRequest } from "@faire/web-api/indigofair/data/ISearchProductsFromBrandRequest";
import { useStrictLocalization } from "@faire/web/common/localization";
import { DEPRECATED_backwardsCompatibleValues } from "@faire/web/common/localization/localize";
import { trackEvent } from "@faire/web/common/trackEvent";
import { Spacer } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import { useMutation } from "@tanstack/react-query";
import React, { useMemo } from "react";
import styled from "styled-components";

import { trackProductClick } from "@faire/retailer/services/Events";
import { MaxWidthContainer } from "@faire/retailer/views/video/components/layout";
import { BrandCatalog } from "@faire/retailer/views/video/MoreProducts/BrandCatalog";

interface IProps {
  brand: Pick<IBrand, "token" | "name">;
  /**
   * Tokens of the products that're already on the page and shouldn't be shown
   * again by this component.
   */
  excludedTokens: string[];
  containerName: string;
}

const NUM_TILES = 16;

export const MoreProducts: React.FC<IProps> = (props) => {
  const { strictLocalize } = useStrictLocalization();
  const { brand, excludedTokens, containerName } = props;

  const { isMobile } = useViewport();

  const {
    mutate: fetchProductTiles,
    data,
    isPending: isLoading,
  } = useMutation({
    mutationFn: async () =>
      searchProductsFromBrand(
        ISearchProductsFromBrandRequest.build({
          brand_token: brand.token,
          include_sales_paused: true,
          sorting_hint: ISearchMakerProductsRequest.ISortingHint.build({
            // This will boost the excluded products to the top. So we're fetching
            // all excluded products and NUM_TILES new products to try get enough
            // new products for this carousel.
            // TODO: we should consider adding a request field to let BE filter
            // out the excluded products
            ordered_product_tokens: excludedTokens,
          }),
          page_size: NUM_TILES + excludedTokens.length,
        })
      ),
  });

  // Hook to fetch product tiles when component mounts
  React.useEffect(() => {
    fetchProductTiles();
  }, [fetchProductTiles]);

  const handleProductClick = (token: string) => {
    trackEvent(`video/more-products/${token}/click`);
    trackProductClick(token, containerName, requestId);
  };

  const dedupedProducts: ILeanProductTile[] = useMemo(() => {
    const excludedTokensSet = new Set(excludedTokens);
    return (
      data?.product_tiles.filter(
        (product_tile) =>
          !excludedTokensSet.has(product_tile.product?.token ?? "")
      ) ?? []
    );
  }, [data?.product_tiles, excludedTokens]);

  const totalProductsCount = data?.pagination_data?.total_results ?? 0;

  const numProducts = dedupedProducts.length;
  const header = excludedTokens.length
    ? strictLocalize(
        {
          defaultMessage: "More from {brandName}",
          description: {
            text: "A list of more products from this brand",
          },
        },
        DEPRECATED_backwardsCompatibleValues({ brandName: brand.name })
      )
    : strictLocalize(
        {
          defaultMessage:
            "{count, plural, one{Product from {brandName}} other{Products from {brandName}}}",
          description: {
            text: "A list of more products from this brand",
          },
        },
        DEPRECATED_backwardsCompatibleValues({
          brandName: brand.name,
          count: numProducts,
        })
      );

  const requestId = data?.request_id;

  if (isLoading || !dedupedProducts.length) {
    return null;
  }

  return (
    <Container>
      <Typography
        variant={isMobile ? "pageHeaderSerifRegular" : "displaySSerifRegular"}
      >
        {header}
      </Typography>
      <Spacer size="4x" />
      <BrandCatalog
        brandToken={brand.token!}
        productTiles={dedupedProducts}
        totalProductsCount={totalProductsCount}
        trackProductClick={handleProductClick}
        loading={false}
      />
    </Container>
  );
};

const Container = styled(MaxWidthContainer)`
  padding-top: 40px;
  padding-bottom: 40px;
`;
